import { PointerEvent, useCallback, useEffect, useRef, useState } from "react";
import { IUsePointerSortProps } from "./types";
import { findScrollContainer } from "./helpers";

/**
 * Cross-input drag-to-reorder using Pointer Events with edge auto-scroll.
 *
 * - Mouse: drag activates after `threshold` px of movement.
 * - Touch / pen: drag activates after `longPressMs` of holding still.
 *   Quick swipes still scroll normally — only a deliberate hold drags.
 * - While dragging, pointing near a scroll-container edge auto-scrolls
 *   that container, so you can drag from the bottom of a long list to
 *   the very top (and vice versa) without releasing.
 *
 * Spread `getItemProps(index)` on every reorderable item.
 */
export function usePointerSort({
  onReorder,
  enabled = true,
  threshold = 6,
  longPressMs = 250,
  edgeAutoScrollPx = 60,
  maxAutoScrollPxPerFrame = 18,
}: IUsePointerSortProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const startRef = useRef<{
    x: number;
    y: number;
    index: number;
    pointerId: number;
    pointerType: string;
    target: HTMLElement;
  } | null>(null);
  const activeRef = useRef(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-scroll state.
  const scrollContainerRef = useRef<HTMLElement | Window | null>(null);
  const scrollVelocityRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const scrollRafRef = useRef<number | null>(null);

  const stopAutoScroll = useCallback(() => {
    scrollVelocityRef.current = { x: 0, y: 0 };
    if (scrollRafRef.current !== null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  }, []);

  const autoScrollTick = useCallback(() => {
    const { x, y } = scrollVelocityRef.current;
    const container = scrollContainerRef.current;
    if ((x === 0 && y === 0) || !container) {
      scrollRafRef.current = null;
      return;
    }
    if (container === window) {
      window.scrollBy(x, y);
    } else {
      (container as HTMLElement).scrollLeft += x;
      (container as HTMLElement).scrollTop += y;
    }
    scrollRafRef.current = requestAnimationFrame(autoScrollTick);
  }, []);

  const updateAutoScrollVelocity = useCallback(
    (clientX: number, clientY: number) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      let top: number;
      let bottom: number;
      let left: number;
      let right: number;
      let canScrollY = false;
      let canScrollX = false;

      if (container === window) {
        top = 0;
        bottom = window.innerHeight;
        left = 0;
        right = window.innerWidth;
        canScrollY = document.documentElement.scrollHeight > window.innerHeight;
        canScrollX = document.documentElement.scrollWidth > window.innerWidth;
      } else {
        const r = (container as HTMLElement).getBoundingClientRect();
        top = r.top;
        bottom = r.bottom;
        left = r.left;
        right = r.right;
        canScrollY =
          (container as HTMLElement).scrollHeight >
          (container as HTMLElement).clientHeight;
        canScrollX =
          (container as HTMLElement).scrollWidth >
          (container as HTMLElement).clientWidth;
      }

      let vy = 0;
      let vx = 0;

      if (canScrollY) {
        if (clientY < top + edgeAutoScrollPx) {
          const ratio = (top + edgeAutoScrollPx - clientY) / edgeAutoScrollPx;
          vy = -Math.min(
            maxAutoScrollPxPerFrame,
            ratio * maxAutoScrollPxPerFrame
          );
        } else if (clientY > bottom - edgeAutoScrollPx) {
          const ratio =
            (clientY - (bottom - edgeAutoScrollPx)) / edgeAutoScrollPx;
          vy = Math.min(
            maxAutoScrollPxPerFrame,
            ratio * maxAutoScrollPxPerFrame
          );
        }
      }

      if (canScrollX) {
        if (clientX < left + edgeAutoScrollPx) {
          const ratio = (left + edgeAutoScrollPx - clientX) / edgeAutoScrollPx;
          vx = -Math.min(
            maxAutoScrollPxPerFrame,
            ratio * maxAutoScrollPxPerFrame
          );
        } else if (clientX > right - edgeAutoScrollPx) {
          const ratio =
            (clientX - (right - edgeAutoScrollPx)) / edgeAutoScrollPx;
          vx = Math.min(
            maxAutoScrollPxPerFrame,
            ratio * maxAutoScrollPxPerFrame
          );
        }
      }

      scrollVelocityRef.current = { x: vx, y: vy };

      if ((vx !== 0 || vy !== 0) && scrollRafRef.current === null) {
        scrollRafRef.current = requestAnimationFrame(autoScrollTick);
      } else if (vx === 0 && vy === 0 && scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    },
    [autoScrollTick, edgeAutoScrollPx, maxAutoScrollPxPerFrame]
  );

  const reset = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    stopAutoScroll();
    scrollContainerRef.current = null;
    startRef.current = null;
    activeRef.current = false;
    setDragIndex(null);
    setOverIndex(null);
  }, [stopAutoScroll]);

  useEffect(() => () => reset(), [reset]);

  const activate = useCallback((index: number) => {
    activeRef.current = true;
    setDragIndex(index);
    const start = startRef.current;
    if (start) {
      try {
        start.target.setPointerCapture(start.pointerId);
      } catch {
        // ignore — already captured or unsupported
      }
      // Snapshot the nearest scrollable ancestor once at drag-activation
      // time so we don't re-walk the DOM every move event.
      scrollContainerRef.current = findScrollContainer(start.target);
    }
  }, []);

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLElement>, index: number) => {
      if (!enabled) return;
      // Only primary button for mouse.
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (longPressTimerRef.current !== null) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      startRef.current = {
        x: e.clientX,
        y: e.clientY,
        index,
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        target: e.currentTarget as HTMLElement,
      };
      activeRef.current = false;

      if (e.pointerType !== "mouse") {
        longPressTimerRef.current = setTimeout(() => {
          longPressTimerRef.current = null;
          if (startRef.current && !activeRef.current) {
            activate(index);
          }
        }, longPressMs);
      }
    },
    [enabled, activate, longPressMs]
  );

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      const start = startRef.current;
      if (!start) return;

      const dx = Math.abs(e.clientX - start.x);
      const dy = Math.abs(e.clientY - start.y);

      if (!activeRef.current) {
        if (start.pointerType === "mouse") {
          if (dx >= threshold || dy >= threshold) activate(start.index);
        } else {
          // Touch / pen movement before long-press = scroll intent, cancel.
          if (dx > threshold || dy > threshold) {
            reset();
            return;
          }
        }
        if (!activeRef.current) return;
      }

      if (e.cancelable) e.preventDefault();

      // Update drop target.
      const node = document.elementFromPoint(e.clientX, e.clientY);
      const item = node?.closest("[data-sort-index]");
      if (item) {
        const idxAttr = item.getAttribute("data-sort-index");
        const idx = idxAttr === null ? NaN : Number(idxAttr);
        if (!Number.isNaN(idx) && idx !== overIndex) {
          setOverIndex(idx);
        }
      }

      // Edge auto-scroll: lets the user reach the top of a long list while
      // dragging from the bottom (and vice versa, plus horizontal).
      updateAutoScrollVelocity(e.clientX, e.clientY);
    },
    [overIndex, threshold, activate, reset, updateAutoScrollVelocity]
  );

  const onPointerUp = useCallback(() => {
    if (
      activeRef.current &&
      dragIndex !== null &&
      overIndex !== null &&
      dragIndex !== overIndex
    ) {
      onReorder(dragIndex, overIndex);
    }
    reset();
  }, [dragIndex, overIndex, onReorder, reset]);

  const onPointerCancel = useCallback(() => reset(), [reset]);

  const getItemProps = useCallback(
    (index: number) => ({
      "data-sort-index": index,
      onPointerDown: (e: PointerEvent<HTMLElement>) => onPointerDown(e, index),
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    }),
    [onPointerDown, onPointerMove, onPointerUp, onPointerCancel]
  );

  return {
    dragIndex,
    overIndex,
    isDragging: dragIndex !== null,
    getItemProps,
  };
}

export default usePointerSort;
